import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Anchor, Eye, EyeOff } from 'lucide-react'
import { useAuth } from '../../core/auth'

export default function Login() {
  const navigate = useNavigate()
  const { user, login } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // Where to land after login depends on which build mode this bundle is
  const POST_LOGIN_PATH =
    __APP_MODE__ === 'ops'  ? '/lng-ops' :
    __APP_MODE__ === 'fsru' ? '/fsru'    :
    '/home'

  useEffect(() => {
    if (user) navigate(POST_LOGIN_PATH, { replace: true })
  }, [user, navigate])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password)
      navigate(POST_LOGIN_PATH)
    } catch (err: any) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex h-screen items-center justify-center bg-bg-primary">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent-muted">
            <Anchor className="h-7 w-7 text-accent" />
          </div>
          <h1 className="mt-4 text-lg font-semibold text-text-primary">BW LNG OPS</h1>
          <p className="text-xs text-text-muted">Operations Dashboard</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="rounded-lg bg-bg-tertiary p-6">
          <div className="flex flex-col gap-4">
            <div>
              <label className="mb-1.5 block text-2xs font-medium uppercase tracking-wider text-text-muted">
                Email
              </label>
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email or username"
                className="w-full rounded-lg bg-bg-secondary px-3 py-2 text-sm text-text-primary placeholder-text-muted outline-none transition-colors focus:border-accent"
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-2xs font-medium uppercase tracking-wider text-text-muted">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className="w-full rounded-lg bg-bg-secondary px-3 py-2 pr-10 text-sm text-text-primary placeholder-text-muted outline-none transition-colors focus:border-accent"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-xs text-negative">{error}</p>
            )}
            <button
              type="submit"
              disabled={loading}
              className="mt-2 w-full rounded-lg bg-accent py-2.5 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-50"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </div>
        </form>

        <p className="mt-4 text-center text-2xs text-text-muted">
          Contact your administrator for access
        </p>
      </div>
    </div>
  )
}
